import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Footer from "../../Footer/FooterBottom";
import { getToken } from "../../../context/auth";
import AiStatsModal from "../../Common/AiStatsModal";

// Import Sub-components
import HistoryDrawer from "./components/HistoryDrawer";
import ChatPane from "./components/ChatPane";
import LessonPane from "./components/LessonPane";
import QuestionPane from "./components/QuestionPane";
import TrophyModal from "./components/TrophyModal";
import WelcomeScreen from "./components/WelcomeScreen";
import CostDetailsModal from "./components/CostDetailsModal";
import RelativeLessonsPane from "./components/RelativeLessonsPane";
import "./style.css";

// Import hooks
import { useSpeechRecognition } from "./hooks/useSpeechRecognition";
import { useFontSize } from "./hooks/useFontSize";
import { useRequestTimer } from "./hooks/useRequestTimer";
import { useSuggestions } from "./hooks/useSuggestions";
import { useChatbotDownload } from "./hooks/useChatbotDownload";
import { useChatbotQuestions } from "./hooks/useChatbotQuestions";

// Import services and helpers
import {
  getOriginalBotMsg,
  getRelativeLinks,
  cleanBotHtml,
  calculateExpectedTime,
} from "./services/chatbotUtils";
import {
  fetchRelativeLessonsDetails,
} from "./services/chatbotService";
import {
  handleDownloadResult,
} from "./services/chatbotDownloadHelper";

import {
  initChatbot,
  deleteChatHistory,
  deleteChatSession,
  clearActiveChat,
  startNewChat,
  loadChatSession,
  stopChatStream,
  sendChatMessage,
  regenerateQuestionsOnly,
  switchToQuestionSet,
  fetchNextTopicRecommendation,
} from "../actions/chatbotActions";

const Chatbot = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  // Redux state selections
  const messages = useSelector((state) => state.chatbot.messages);
  const history = useSelector((state) => state.chatbot.history);
  const loading = useSelector((state) => state.chatbot.loading);
  const error = useSelector((state) => state.chatbot.error);
  const errorStatus = useSelector((state) => state.chatbot.errorStatus);
  const activeSessionId = useSelector((state) => state.chatbot.activeSessionId);
  const totalPrice = useSelector((state) => state.chatbot.totalPrice);
  const topicRecommendation = useSelector((state) => state.chatbot.topicRecommendation);
  const user = useSelector((state) => state.user);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [fullscreenMessageHtml, setFullscreenMessageHtml] = useState(null);
  const [fullscreenMessageQuestion, setFullscreenMessageQuestion] = useState(null);
  const [expandedStatsIndex, setExpandedStatsIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("lesson"); // "lesson" or "questions"
  const [mobileActiveTab, setMobileActiveTab] = useState("lesson"); // "chat", "lesson", "questions"
  const [showTrophyModal, setShowTrophyModal] = useState(false);
  const [chatScreen, setChatScreen] = useState("welcome"); // "welcome" | "workspace"
  const [pendingTopic, setPendingTopic] = useState("");
  const [isLocallyLoading, setIsLocallyLoading] = useState(false);
  const [passCash, setPassCash] = useState(false);
  const [model, setModel] = useState("deepseek");
  const [showKpis, setShowKpis] = useState(() => {
    const saved = localStorage.getItem("chatbot_show_kpis");
    return saved === null ? false : saved === "true";
  });

  const handleToggleKpis = (val) => {
    setShowKpis(val);
    localStorage.setItem("chatbot_show_kpis", val ? "true" : "false");
  };

  const [costModalOpen, setCostModalOpen] = useState(false);
  const [isChatPaneExpanded, setIsChatPaneExpanded] = useState(false);
  const [downloadSubChat, setDownloadSubChat] = useState(true);
  const [downloadQuestions, setDownloadQuestions] = useState(true);
  const [downloadRelativeLessons, setDownloadRelativeLessons] = useState(true);
  const [showChatStats, setShowChatStats] = useState(true);
  const [relativeLessonsDetails, setRelativeLessonsDetails] = useState([]);
  const [fetchingRelativeLessons, setFetchingRelativeLessons] = useState(false);
  const [difficulty, setDifficulty] = useState("mid");
  const [isAnExam, setIsAnExam] = useState(false);
  const [isWorkspaceExpanded, setIsWorkspaceExpanded] = useState(false);
  const [questionsCount, setQuestionsCount] = useState(10);
  const [isRegeneratingQuestions, setIsRegeneratingQuestions] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [tempQuestionsCount, setTempQuestionsCount] = useState(10);
  const [tempDifficulty, setTempDifficulty] = useState("mid");
  const [isChangingMode, setIsChangingMode] = useState(false);
  const [changingModeText, setChangingModeText] = useState("");
  const [isSwitchingHistory, setIsSwitchingHistory] = useState(false);
  const [showNextTopicModal, setShowNextTopicModal] = useState(false);
  const [selectedRecommendIdx, setSelectedRecommendIdx] = useState(0);

  const handleOpenNextTopicModal = () => {
    setSelectedRecommendIdx(0);
    setShowNextTopicModal(true);
  };

  // Initialize hooks
  const speechRec = useSpeechRecognition(t);
  const font = useFontSize();
  const timer = useRequestTimer();
  const sugg = useSuggestions();
  const downloadHook = useChatbotDownload();
  const questionsHook = useChatbotQuestions({
    messages,
    activeSessionId,
    loading,
    sendChatMessage: (text, pass, mod, diff, isEval) =>
      dispatch(sendChatMessage(text, pass, mod, diff, isEval)),
    passCash,
    model,
    difficulty,
    isAnExam,
    t,
  });

  const handleToggleSidebar = () => {
    setHistoryDrawerOpen((p) => !p);
  };

  const handleCloseSidebar = () => {
    setHistoryDrawerOpen(false);
  };

  const handleMoveToQuestions = () => {
    setActiveTab("questions");
    setMobileActiveTab("questions");
  };

  const handleExpandLesson = (html) => {
    const activeSession = history.find((h) => h.id === activeSessionId);
    const sessionTitle = activeSession ? activeSession.title : "";
    setFullscreenMessageHtml(html);
    setFullscreenMessageQuestion(sessionTitle);
  };

  const handleToggleChatExpand = () => {
    setIsChatPaneExpanded((p) => !p);
  };

  const handleToggleWorkspaceExpand = () => {
    setIsWorkspaceExpanded((p) => !p);
  };

  const handleRegenerateQuestions = async (overrideDifficulty, overrideQuestionsCount) => {
    if (isRegeneratingQuestions || !lessonHtml) return;
    setIsRegeneratingQuestions(true);

    const activeDifficulty = overrideDifficulty !== undefined ? overrideDifficulty : difficulty;
    const activeQuestionsCount = overrideQuestionsCount !== undefined ? overrideQuestionsCount : questionsCount;

    try {
      questionsHook.resetQuestionsState();
      await dispatch(
        regenerateQuestionsOnly(
          lessonHtml,
          activeDifficulty,
          model,
          activeQuestionsCount
        )
      );
    } catch (err) {
      console.error("Error regenerating questions:", err);
    } finally {
      setIsRegeneratingQuestions(false);
    }
  };

  const handleShowRegenerateConfirm = () => {
    setTempQuestionsCount(questionsCount);
    setTempDifficulty(difficulty);
    setShowRegenerateConfirm(true);
  };

  const handleConfirmRegenerate = () => {
    setDifficulty(tempDifficulty);
    setQuestionsCount(tempQuestionsCount);
    setShowRegenerateConfirm(false);
    handleRegenerateQuestions(tempDifficulty, tempQuestionsCount);
  };

  const handleIsAnExamChange = (val) => {
    setIsChangingMode(true);
    setChangingModeText(
      val
        ? t("Pages.Chatbot.SwitchingToExamMode") || "جاري الانتقال إلى وضع الاختبار... يرجى الانتظار"
        : t("Pages.Chatbot.SwitchingToPracticeMode") || "جاري الانتقال إلى وضع التمرين... يرجى الانتظار"
    );
    setTimeout(() => {
      setIsAnExam(val);
      setIsChangingMode(false);
    }, 1500);
  };

  const handleExpandChat = (html, question) => {
    setFullscreenMessageHtml(html);
    setFullscreenMessageQuestion(question);
  };

  const handleShowChatStatsChange = (val) => {
    setShowChatStats(val);
  };

  const handleDownloadFullChat = () => {
    if (!messages || messages.length === 0) return;
    downloadHook.setDownloadExplanationOption(true);
    downloadHook.setDownloadChatOption(true);
    downloadHook.setDownloadQuestionsOption(true);
    downloadHook.setDownloadRelativeLessonsOption(true);
    downloadHook.setDownloadSolvedOption(true);
    downloadHook.setDownloadStudentAnswersOption(true);
    downloadHook.setDownloadHintsOption(true);
    downloadHook.setDownloadModalOpen(true);
  };

  const handleResend = () => {
    const lastUserMsg = messages
      .slice()
      .reverse()
      .find((m) => m.sender === "user");
    if (lastUserMsg) {
      dispatch(
        sendChatMessage(
          lastUserMsg.text,
          passCash,
          model,
          difficulty,
          lastUserMsg.isEvaluation || false
        )
      );
    }
  };

  const handleSessionClick = (session) => {
    dispatch(loadChatSession(session));
    questionsHook.resetQuestionsState();
    setChatScreen(
      session.messages && session.messages.length > 0 ? "workspace" : "welcome"
    );
    setPendingTopic("");
    setMobileActiveTab("lesson");
  };

  const handleNewChat = () => {
    dispatch(startNewChat());
    questionsHook.resetQuestionsState();
    setChatScreen("welcome");
    setPendingTopic("");
    setMobileActiveTab("lesson");
    setIsLocallyLoading(false);
  };

  const handleConfirmNextTopic = () => {
    if (!topicRecommendation) return;
    let nextTopic = "";
    if (topicRecommendation.choices && topicRecommendation.choices.length > 0) {
      const selectedChoice = topicRecommendation.choices[selectedRecommendIdx];
      if (selectedChoice) {
        nextTopic = selectedChoice.topicName;
      }
    } else {
      nextTopic = topicRecommendation.topicName;
    }

    if (!nextTopic) return;

    dispatch(startNewChat());
    questionsHook.resetQuestionsState();
    setPendingTopic(nextTopic);
    setIsLocallyLoading(true);
    setChatScreen("welcome");
    setShowNextTopicModal(false);
    setMobileActiveTab("lesson");
    lastFetchedRelativeLessonsJson.current = null;
    dispatch(sendChatMessage(nextTopic, passCash, model, difficulty));
  };

  const handleDeleteHistory = () => {
    setConfirmDelete(true);
  };

  const handleClearChat = () => {
    setConfirmClear(true);
  };

  // Mount logic
  useEffect(() => {
    dispatch(initChatbot());

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setFullscreenMessageHtml(null);
        setFullscreenMessageQuestion(null);
        setExpandedStatsIndex(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch]);

  // Loading states and timer triggers
  const { startRequestTimer, stopRequestTimer } = timer;
  useEffect(() => {
    if (loading) {
      startRequestTimer();
    } else {
      stopRequestTimer();
      setIsLocallyLoading(false);
    }
  }, [loading, startRequestTimer, stopRequestTimer]);

  // Check for questions in messages
  const originalBotMsg = getOriginalBotMsg(messages);
  const originalBotMsgIndex = messages ? messages.indexOf(originalBotMsg) : -1;
  useEffect(() => {
    questionsHook.checkForQuestions(
      originalBotMsg,
      font,
      activeTab,
      setActiveTab,
      setMobileActiveTab,
      setIsSwitchingHistory
    );
  }, [originalBotMsg, activeSessionId, loading, font, questionsHook, activeTab]);

  // Fetch next topic recommendation after first bot message loaded
  const firstBotMsg = messages ? messages.find((m) => m.sender === "bot") : null;
  const hasFirstBotMsgLoaded = firstBotMsg && !firstBotMsg.isStreaming;

  useEffect(() => {
    if (hasFirstBotMsgLoaded && !topicRecommendation && activeSessionId && !loading) {
      const firstUserMsg = messages.find((m) => m.sender === "user");
      const currentTopic = firstUserMsg ? firstUserMsg.text : "";
      if (currentTopic) {
        dispatch(fetchNextTopicRecommendation(currentTopic, model));
      }
    }
  }, [hasFirstBotMsgLoaded, topicRecommendation, activeSessionId, loading, messages, model, dispatch]);

  // Relative lessons logic
  const lastFetchedRelativeLessonsJson = useRef(null);
  const fetchRelativeLessonsDetailsData = useCallback(async (relativeLessons) => {
    if (
      !relativeLessons ||
      !Array.isArray(relativeLessons) ||
      relativeLessons.length === 0
    ) {
      setRelativeLessonsDetails((prev) => (prev.length > 0 ? [] : prev));
      setFetchingRelativeLessons(false);
      return;
    }

    const relativeLessonsJson = JSON.stringify(relativeLessons);
    if (lastFetchedRelativeLessonsJson.current === relativeLessonsJson) {
      return;
    }
    lastFetchedRelativeLessonsJson.current = relativeLessonsJson;

    setFetchingRelativeLessons(true);
    setRelativeLessonsDetails([]);

    try {
      const details = await fetchRelativeLessonsDetails(relativeLessons);
      setRelativeLessonsDetails(details);
    } catch (err) {
      console.error("Failed to fetch relative lessons details:", err);
    } finally {
      setFetchingRelativeLessons(false);
    }
  }, []);

  useEffect(() => {
    const relativeLessons = originalBotMsg ? originalBotMsg.relativeLessons : null;
    fetchRelativeLessonsDetailsData(relativeLessons);
  }, [originalBotMsg, activeSessionId, fetchRelativeLessonsDetailsData]);

  // Handle waiting for evaluation
  useEffect(() => {
    if (questionsHook.isWaitingForEvaluation) {
      const lastBotMsg = messages
        .slice()
        .reverse()
        .find((m) => m.sender === "bot");
      if (lastBotMsg) {
        const cleanedHtml = cleanBotHtml(lastBotMsg.text);
        if (cleanedHtml !== questionsHook.finalEvaluationHtml) {
          questionsHook.setFinalEvaluationHtml(cleanedHtml);
        }
        if (!loading) {
          questionsHook.setIsWaitingForEvaluation(false);
          questionsHook.setShowFinalReportCard(true);
        }
      }
    }
  }, [messages, loading, questionsHook]);

  // Two-screen flow transition logic
  useEffect(() => {
    const hasStreamingStarted = messages.some(
      (m) =>
        m.sender === "bot" &&
        m.text &&
        m.text.replace(/<[^>]*>/g, "").trim() !== ""
    );
    if (chatScreen === "welcome" && hasStreamingStarted) {
      setChatScreen("workspace");
    }
  }, [messages, chatScreen]);

  // When messages are cleared (new chat), go back to welcome screen and reset state
  const prevMessagesLength = useRef(0);
  useEffect(() => {
    if (
      !loading &&
      prevMessagesLength.current > 0 &&
      messages.length === 0 &&
      (chatScreen !== "welcome" ||
        questionsHook.activeQuestions !== null ||
        pendingTopic !== "")
    ) {
      setChatScreen("welcome");
      setPendingTopic("");
      setMobileActiveTab("lesson");
      questionsHook.resetQuestionsState();
      lastFetchedRelativeLessonsJson.current = null;
    }
    prevMessagesLength.current = messages.length;
  }, [messages, loading, chatScreen, questionsHook, pendingTopic]);

  if (!getToken()) {
    return (
      <>
        <Helmet>
          <title>
            {t("Pages.Chatbot.title")} | {t("alarabeyya")}
          </title>
        </Helmet>
        <div className="chatbot-page-section login-required">
          <div className="gemini-bg-glows welcome-screen-bg-active">
            <div className="glow-blob glow-purple"></div>
            <div className="glow-blob glow-green"></div>
            <div className="glow-blob glow-navy"></div>
            <div className="glow-blob glow-gold"></div>
          </div>
          <div className="lock-card">
            <div className="lock-icon-container">
              <i className="fal fa-user-lock"></i>
            </div>
            <h3 className="lock-title">{t("Pages.Chatbot.LoginRequired")}</h3>
            <p className="lock-text">{t("Pages.Chatbot.LoginRequiredDesc")}</p>
            <a href="/Login" className="login-cta-btn">
              <i className="fal fa-sign-in-alt"></i>
              <span>{t("Login")}</span>
            </a>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relativeLinks = getRelativeLinks(messages);
  const expectedTimeMins = calculateExpectedTime(messages);
  const hasRelative =
    (originalBotMsg &&
      originalBotMsg.relativeLessons &&
      originalBotMsg.relativeLessons.length > 0) ||
    (relativeLinks && relativeLinks.length > 0);
  const hasQuestionsInData = !!(
    originalBotMsg &&
    (originalBotMsg.text?.includes("lesson-questions-data") ||
      (originalBotMsg.questions && originalBotMsg.questions.length > 0))
  );
  const showQuestionsTab =
    (questionsHook.activeQuestions && questionsHook.activeQuestions.length > 0) ||
    hasQuestionsInData;
  const isStartChatFinished =
    messages &&
    messages.length >= 2 &&
    messages.some((m) => m.sender === "bot" && !m.isStreaming);

  const lessonHtml = originalBotMsg ? cleanBotHtml(originalBotMsg.text) : "";
  const activeSession = history.find((h) => h.id === activeSessionId);
  const sessionTitle = activeSession ? activeSession.title : "";
  const lastMsg = messages && messages.length > 0 ? messages[messages.length - 1] : null;
  const cleanedText = originalBotMsg ? cleanBotHtml(originalBotMsg.text) : "";
  const visibleText = cleanedText.replace(/<[^>]*>/g, "").trim();
  const isMainLessonLoading =
    !originalBotMsg ||
    (originalBotMsg &&
      originalBotMsg.isLesson &&
      originalBotMsg.isStreaming &&
      visibleText === "");
  const showStreamWaitingPopup =
    (loading || isLocallyLoading) && isMainLessonLoading;
  const isQuestionsLoading =
    hasQuestionsInData &&
    (!questionsHook.activeQuestions || questionsHook.activeQuestions.length === 0);
  const isRelativeLoading =
    originalBotMsg &&
    originalBotMsg.relativeLessons &&
    originalBotMsg.relativeLessons.length > 0 &&
    (fetchingRelativeLessons || !relativeLessonsDetails || relativeLessonsDetails.length === 0);

  const extractTitleFromHtml = (html) => {
    if (!html) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const heading = doc.querySelector("h1, h2, h3");
      if (heading && heading.textContent.trim()) {
        return heading.textContent
          .replace(/[\n\r\t]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }
    } catch (e) {
      console.error("Failed to extract title from HTML:", e);
    }
    return "";
  };

  return (
    <>
      <Helmet>
        <title>
          {t("Pages.Chatbot.title")} | {t("alarabeyya")}
        </title>
      </Helmet>
      <section
        className={`chatbot-page-section ${chatScreen === "welcome" ? "welcome-screen-active" : ""
          } ${historyDrawerOpen ? "sidebar-drawer-open" : ""}`}
      >
        <div
          className={`gemini-bg-glows ${chatScreen === "welcome" ? "welcome-screen-bg-active" : ""
            }`}
        >
          <div className="glow-blob glow-purple"></div>
          <div className="glow-blob glow-green"></div>
          <div className="glow-blob glow-navy"></div>
          <div className="glow-blob glow-gold"></div>
        </div>
        <div
          className={`chatbot-main-container ${historyDrawerOpen ? "sidebar-open" : "sidebar-closed"
            }`}
        >
          <HistoryDrawer
            isOpen={historyDrawerOpen}
            onClose={handleCloseSidebar}
            onToggle={handleToggleSidebar}
            searchTerm={searchTerm}
            onSearchChange={(val) => setSearchTerm(val)}
            onNewChat={handleNewChat}
            onDeleteHistory={handleDeleteHistory}
            history={history}
            activeSessionId={activeSessionId}
            onSessionClick={handleSessionClick}
            onDeleteSession={(id) => dispatch(deleteChatSession(id))}
            t={t}
            passCash={passCash}
            model={model}
            showKpis={showKpis}
            onPassCashChange={(val) => setPassCash(val)}
            onModelChange={(val) => setModel(val)}
            onToggleKpis={handleToggleKpis}
            messages={messages}
            onDownloadFullChat={handleDownloadFullChat}
            onClearChat={handleClearChat}
            downloadSubChat={downloadSubChat}
            downloadQuestions={downloadQuestions}
            downloadRelativeLessons={downloadRelativeLessons}
            onDownloadSubChatChange={(val) => setDownloadSubChat(val)}
            onDownloadQuestionsChange={(val) => setDownloadQuestions(val)}
            onDownloadRelativeLessonsChange={(val) => setDownloadRelativeLessons(val)}
            showChatStats={showChatStats}
            onShowChatStatsChange={handleShowChatStatsChange}
            difficulty={difficulty}
            isAnExam={isAnExam}
            onDifficultyChange={(val) => setDifficulty(val)}
            onIsAnExamChange={handleIsAnExamChange}
            questionsCount={questionsCount}
            onQuestionsCountChange={(val) => setQuestionsCount(val)}
          />
          {!historyDrawerOpen && (
            <div
              className="mobile-sidebar-toggle-btn d-lg-none animate__animated animate__fadeIn animate__faster"
              onClick={handleToggleSidebar}
              title={t("Pages.Chatbot.OpenMenu")}
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <i className="fal fa-bars"></i>
            </div>
          )}
          <div className="chatbot-content-area" style={{ position: "relative" }}>
            {chatScreen === "welcome" ? (
              <WelcomeScreen
                t={t}
                loading={loading || isLocallyLoading}
                isWelcomeListening={speechRec.isWelcomeListening}
                toggleWelcomeSpeech={speechRec.toggleWelcomeSpeech}
                error={errorStatus === "unsupported" ? null : error}
                errorStatus={errorStatus}
                suggestions={sugg.suggestions}
                onStartSearch={(topic, topicId) => {
                  setPendingTopic(topic);
                  setIsLocallyLoading(true);
                  dispatch(sendChatMessage(topic, passCash, model, difficulty, false, 10, topicId));
                }}
              />
            ) : (
              <div className="chatbot-workspace-wrapper d-flex flex-column h-100 p-2 p-md-3 animate__animated animate__fadeIn">
                {/* KPI Dashboard */}
                {showKpis && messages && messages.length > 0 && (
                  <div className="px-2 mb-3 animate__animated animate__fadeIn w-100 d-none d-md-block" style={{ flexShrink: 0 }}>
                    <div className="chat-kpi-container position-relative">
                      {/* Desktop Close button for KPI */}
                      <button
                        type="button"
                        className="close position-absolute"
                        style={{
                          left: "15px",
                          top: "10px",
                          border: "none",
                          background: "none",
                          fontSize: "1.2rem",
                          color: "#999",
                          cursor: "pointer",
                          zIndex: 10,
                          width: "auto"
                        }}
                        onClick={() => handleToggleKpis(false)}
                      >
                        <i className="fal fa-times"></i>
                      </button>

                      <div className="row no-gutters align-items-stretch">
                        <div className="col-12 col-md-3 px-2 mb-2 mb-md-0">
                          <div
                            className="kpi-card clickable h-100"
                            onClick={() => setCostModalOpen(true)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <div className="kpi-icon text-success">
                                <i className="fal fa-file-invoice-dollar"></i>
                              </div>
                              <div className="kpi-info mr-2 text-right">
                                <span className="kpi-label">
                                  {t("Pages.Chatbot.TotalCost") || "التكلفة الإجمالية"}
                                </span>
                                <span className="kpi-value d-block font-weight-bold">
                                  {(totalPrice || 0).toFixed(5)}$
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-3 px-2 mb-2 mb-md-0 border-right-divider">
                          <div className="kpi-card h-100">
                            <div className="d-flex align-items-center gap-2">
                              <div className="kpi-icon text-primary">
                                <i className="fal fa-comments"></i>
                              </div>
                              <div className="kpi-info mr-2 text-right">
                                <span className="kpi-label">
                                  {t("Pages.Chatbot.MessagesCount") || "عدد الرسائل"}
                                </span>
                                <span className="kpi-value d-block font-weight-bold">
                                  {messages.length}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-3 px-2 mb-2 mb-md-0 border-right-divider">
                          <div className="kpi-card h-100">
                            <div className="d-flex align-items-center gap-2">
                              <div className="kpi-icon text-info">
                                <i className="fal fa-brain"></i>
                              </div>
                              <div className="kpi-info mr-2 text-right">
                                <span className="kpi-label">
                                  {t("Pages.Chatbot.Model") || "النموذج النشط"}
                                </span>
                                <span className="kpi-value d-block font-weight-bold">
                                  {model === "gemini"
                                    ? "Gemini 3.1 Lite"
                                    : "DeepSeek Flash"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-3 px-2 mb-2 mb-md-0 border-right-divider">
                          <div className="kpi-card h-100">
                            <div className="d-flex align-items-center gap-2">
                              <div className="kpi-icon text-warning">
                                <i className="fal fa-hourglass-half"></i>
                              </div>
                              <div className="kpi-info mr-2 text-right">
                                <span className="kpi-label">
                                  {t("Pages.Chatbot.EstimatedLearningTime") ||
                                    "وقت التعلم المتوقع"}
                                </span>
                                <span className="kpi-value d-block font-weight-bold">
                                  {expectedTimeMins}{" "}
                                  {expectedTimeMins === 1
                                    ? t("Pages.Chatbot.Minute") || "دقيقة"
                                    : t("Pages.Chatbot.Minutes") || "دقائق"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Main columns wrapper for Left/Right panels to prevent vertical overflow */}
                <div className="row no-gutters flex-grow-1 w-100" style={{ minHeight: 0 }}>
                  {/* Left Panel: Workspace (Lesson and Questions) */}
                  <div
                    className={`chatbot-pane-wrapper col-12 ${isStartChatFinished
                      ? (isChatPaneExpanded ? "d-none" : (isWorkspaceExpanded ? "col-lg-12" : "col-lg-7"))
                      : "col-lg-12"
                      } h-100 px-lg-2 ${mobileActiveTab === "chat" ? "mobile-hidden" : "mobile-active"
                      }`}
                  >
                    <div className="glass-card">
                      {mobileActiveTab === "kpis" ? (
                        <div className="mobile-kpis-only-view p-3 p-md-4 overflow-auto text-right w-100" style={{ direction: "rtl", height: "100%" }}>
                          <h3
                            className="h5 font-weight-bold mb-4 d-flex align-items-center justify-content-between"
                            style={{ color: "#0e2c53" }}
                          >
                            <span className="d-flex align-items-center">
                              <i className="fal fa-analytics ml-2 text-primary" style={{ color: "#92278f" }}></i>
                              <span>{t("Pages.Chatbot.ShowKpis") || "مؤشرات الأداء"}</span>
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-muted p-0"
                              onClick={() => {
                                handleToggleKpis(false);
                                setMobileActiveTab("lesson");
                              }}
                              style={{ fontSize: "1.2rem", width: "auto" }}
                            >
                              <i className="fal fa-times"></i>
                            </button>
                          </h3>

                          <div className="row no-gutters">
                            <div className="col-12 mb-3">
                              <div
                                className="kpi-card clickable"
                                onClick={() => setCostModalOpen(true)}
                                style={{
                                  cursor: "pointer",
                                  background: "#fff",
                                  border: "1px solid rgba(14, 44, 83, 0.08)",
                                  borderRadius: "16px",
                                  boxShadow: "0 4px 15px rgba(14, 44, 83, 0.03)",
                                  padding: "18px",
                                  display: "flex",
                                  alignItems: "center"
                                }}
                              >
                                <div className="d-flex align-items-center gap-3">
                                  <div className="kpi-icon text-success" style={{ fontSize: "1.8rem" }}>
                                    <i className="fal fa-file-invoice-dollar"></i>
                                  </div>
                                  <div className="kpi-info mr-3 text-right">
                                    <span className="kpi-label text-muted small d-block mb-1">
                                      {t("Pages.Chatbot.TotalCost") || "التكلفة الإجمالية"}
                                    </span>
                                    <span className="kpi-value d-block font-weight-bold h5 mb-0">
                                      {(totalPrice || 0).toFixed(5)}$
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 mb-3">
                              <div
                                className="kpi-card"
                                style={{
                                  background: "#fff",
                                  border: "1px solid rgba(14, 44, 83, 0.08)",
                                  borderRadius: "16px",
                                  boxShadow: "0 4px 15px rgba(14, 44, 83, 0.03)",
                                  padding: "18px",
                                  display: "flex",
                                  alignItems: "center"
                                }}
                              >
                                <div className="d-flex align-items-center gap-3">
                                  <div className="kpi-icon text-primary" style={{ fontSize: "1.8rem" }}>
                                    <i className="fal fa-comments"></i>
                                  </div>
                                  <div className="kpi-info mr-3 text-right">
                                    <span className="kpi-label text-muted small d-block mb-1">
                                      {t("Pages.Chatbot.MessagesCount") || "عدد الرسائل"}
                                    </span>
                                    <span className="kpi-value d-block font-weight-bold h5 mb-0">
                                      {messages.length}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 mb-3">
                              <div
                                className="kpi-card"
                                style={{
                                  background: "#fff",
                                  border: "1px solid rgba(14, 44, 83, 0.08)",
                                  borderRadius: "16px",
                                  boxShadow: "0 4px 15px rgba(14, 44, 83, 0.03)",
                                  padding: "18px",
                                  display: "flex",
                                  alignItems: "center"
                                }}
                              >
                                <div className="d-flex align-items-center gap-3">
                                  <div className="kpi-icon text-info" style={{ fontSize: "1.8rem" }}>
                                    <i className="fal fa-brain"></i>
                                  </div>
                                  <div className="kpi-info mr-3 text-right">
                                    <span className="kpi-label text-muted small d-block mb-1">
                                      {t("Pages.Chatbot.Model") || "النموذج النشط"}
                                    </span>
                                    <span className="kpi-value d-block font-weight-bold h5 mb-0">
                                      {model === "gemini"
                                        ? "Gemini 3.1 Lite"
                                        : "DeepSeek Flash"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 mb-3">
                              <div
                                className="kpi-card"
                                style={{
                                  background: "#fff",
                                  border: "1px solid rgba(14, 44, 83, 0.08)",
                                  borderRadius: "16px",
                                  boxShadow: "0 4px 15px rgba(14, 44, 83, 0.03)",
                                  padding: "18px",
                                  display: "flex",
                                  alignItems: "center"
                                }}
                              >
                                <div className="d-flex align-items-center gap-3">
                                  <div className="kpi-icon text-warning" style={{ fontSize: "1.8rem" }}>
                                    <i className="fal fa-hourglass-half"></i>
                                  </div>
                                  <div className="kpi-info mr-3 text-right">
                                    <span className="kpi-label text-muted small d-block mb-1">
                                      {t("Pages.Chatbot.EstimatedLearningTime") ||
                                        "وقت التعلم المتوقع"}
                                    </span>
                                    <span className="kpi-value d-block font-weight-bold h5 mb-0">
                                      {expectedTimeMins}{" "}
                                      {expectedTimeMins === 1
                                        ? t("Pages.Chatbot.Minute") || "دقيقة"
                                        : t("Pages.Chatbot.Minutes") || "دقائق"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="workspace-tab-bar">
                            <button
                              className={`workspace-tab-btn ${activeTab === "lesson" ? "active" : ""
                                }`}
                              onClick={() => setActiveTab("lesson")}
                            >
                              <i className="fal fa-book-open"></i>
                              <span>{t("Pages.Chatbot.GeneratedLesson")}</span>
                            </button>
                            {showQuestionsTab && (
                              <button
                                className={`workspace-tab-btn ${activeTab === "questions" ? "active" : ""
                                  }`}
                                onClick={() => setActiveTab("questions")}
                              >
                                <i className="fal fa-graduation-cap"></i>
                                <span>{t("Pages.Chatbot.InteractiveExercises")}</span>
                                {questionsHook.activeQuestions &&
                                  questionsHook.activeQuestions.length > 0 ? (
                                  <span className="workspace-tab-badge animate__animated animate__fadeIn">
                                    {questionsHook.activeQuestions.length}
                                  </span>
                                ) : (
                                  <span
                                    className="workspace-tab-spinner ml-1"
                                    style={{ color: "#92278f" }}
                                  >
                                    <i className="fas fa-spinner fa-spin small"></i>
                                  </span>
                                )}
                              </button>
                            )}
                            {hasRelative && (
                              <button
                                className={`workspace-tab-btn ${activeTab === "relativeLessons" ? "active" : ""
                                  }`}
                                onClick={() => setActiveTab("relativeLessons")}
                              >
                                <i className="fal fa-link"></i>
                                <span>{t("Pages.Chatbot.RelativeLessonsTab")}</span>
                                {fetchingRelativeLessons ? (
                                  <span
                                    className="workspace-tab-spinner ml-1"
                                    style={{ color: "#92278f" }}
                                  >
                                    <i className="fas fa-spinner fa-spin small"></i>
                                  </span>
                                ) : (
                                  <span className="workspace-tab-badge animate__animated animate__fadeIn">
                                    {originalBotMsg?.relativeLessons?.length ||
                                      relativeLinks.length}
                                  </span>
                                )}
                              </button>
                            )}
                            {topicRecommendation && topicRecommendation.topicName && (
                              <button
                                className="workspace-tab-btn next-topic-tab-btn animate__animated animate__pulse animate__infinite animate__slower"
                                onClick={handleOpenNextTopicModal}
                                style={{
                                  borderColor: "rgba(146, 39, 143, 0.3)",
                                  background: "rgba(146, 39, 143, 0.05)",
                                }}
                              >
                                <i className="fal fa-rocket text-primary animate__animated animate__bounce animate__infinite animate__slower" style={{ color: "#92278f" }}></i>
                                <span style={{ color: "#92278f", fontWeight: "bold" }}>
                                  {t("Pages.Chatbot.NextLearnTab") || "ماذا أتعلم بعد ذلك؟"}
                                </span>
                              </button>
                            )}
                            {isStartChatFinished && (
                              <div className="chat-header-actions mr-auto pl-3 d-flex align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-secondary p-1"
                                  onClick={handleToggleWorkspaceExpand}
                                  title={isWorkspaceExpanded ? t("Pages.Chatbot.CollapseWorkspace") : t("Pages.Chatbot.ExpandWorkspace")}
                                  style={{ width: 'auto' }}
                                >
                                  <i className={`fal ${isWorkspaceExpanded ? "fa-compress-arrows-alt" : "fa-arrows-alt-h"}`} style={{ fontSize: '16px' }}></i>
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="workspace-tab-content">
                            <div
                              className={`workspace-tab-pane ${activeTab === "lesson" ? "active" : ""
                                }`}
                            >
                              <LessonPane
                                lessonHtml={lessonHtml}
                                hasQuestions={
                                  questionsHook.activeQuestions &&
                                  questionsHook.activeQuestions.length > 0
                                }
                                onMoveToQuestions={handleMoveToQuestions}
                                onExpand={handleExpandLesson}
                                loading={loading}
                                t={t}
                                title={sessionTitle}
                                onToggleSidebar={handleToggleSidebar}
                                originalBotMsg={originalBotMsg}
                                originalBotMsgIndex={originalBotMsgIndex}
                                onToggleStats={(idx) => setExpandedStatsIndex(idx)}
                                onDownload={handleDownloadResult}
                                showChatStats={showChatStats}
                                isWorkspaceExpanded={isWorkspaceExpanded}
                                onToggleWorkspaceExpand={handleToggleWorkspaceExpand}
                              />
                            </div>
                            {showQuestionsTab && (
                              <div
                                className={`workspace-tab-pane ${activeTab === "questions" ? "active" : ""
                                  }`}
                              >
                                {questionsHook.activeQuestions &&
                                  questionsHook.activeQuestions.length > 0 ? (
                                  <QuestionPane
                                    activeQuestions={questionsHook.activeQuestions}
                                    relativeLessons={relativeLessonsDetails}
                                    user={user}
                                    currentQuestionIndex={questionsHook.currentQuestionIndex}
                                    solvedQuestions={questionsHook.solvedQuestions}
                                    examAnswers={questionsHook.examAnswers}
                                    saveExamAnswer={questionsHook.saveExamAnswer}
                                    handlePrevQuestion={questionsHook.handlePrevQuestion}
                                    handleNextQuestion={questionsHook.handleNextQuestion}
                                    questionOverlay={questionsHook.questionOverlay}
                                    onCloseOverlay={questionsHook.closeQuestionOverlay}
                                    onShowTrophyModal={() => {
                                      if (isAnExam) {
                                        questionsHook.handleFinishAttempt();
                                      } else {
                                        questionsHook.evaluateCurrentQuestionAndNavigate();
                                      }
                                    }}
                                    handleExitSession={questionsHook.handleExitSession}
                                    showFinalReportCard={questionsHook.showFinalReportCard}
                                    isWaitingForEvaluation={
                                      questionsHook.isWaitingForEvaluation
                                    }
                                    finalEvaluationHtml={questionsHook.finalEvaluationHtml}
                                    sessionStartTime={questionsHook.sessionStartTime}
                                    moreFontSize={font.moreFontSize}
                                    lessFontSize={font.lessFontSize}
                                    resetFontSize={() =>
                                      font.resetFontSize(questionsHook.activeQuestions)
                                    }
                                    FontSize={font.FontSize}
                                    questionSeparated={font.questionSeparated}
                                    title={sessionTitle}
                                    t={t}
                                    onToggleSidebar={handleToggleSidebar}
                                    isAnExam={isAnExam}
                                    onIsAnExamChange={handleIsAnExamChange}
                                    onShowHint={questionsHook.onShowHint}
                                    showedHints={questionsHook.showedHints}
                                    onResetAttempt={questionsHook.handleResetEvaluationAndSolveAgain}
                                    onRegenerateQuestions={handleShowRegenerateConfirm}
                                    loading={isRegeneratingQuestions}
                                    isWorkspaceExpanded={isWorkspaceExpanded}
                                    onToggleWorkspaceExpand={handleToggleWorkspaceExpand}
                                    onGoToQuestion={questionsHook.transitionQuestion}
                                    originalBotMsg={originalBotMsg}
                                    originalBotMsgIndex={originalBotMsgIndex}
                                    onSwitchToQuestionSet={(historyIndex) => {
                                      setIsSwitchingHistory(true);
                                      dispatch(switchToQuestionSet(originalBotMsgIndex, historyIndex));
                                    }}
                                  />
                                ) : (
                                  <div className="text-center py-5">
                                    <div className="stream-waiting-spinner-container">
                                      <div className="stream-waiting-spinner-outer"></div>
                                      <div className="stream-waiting-spinner-inner"></div>
                                      <i className="fal fa-robot stream-waiting-icon"></i>
                                    </div>
                                    <h4
                                      style={{
                                        color: "#0e2c53",
                                        fontFamily: '"Cairo", sans-serif',
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {t("Pages.Chatbot.LoadingQuestions")}
                                    </h4>
                                    <p className="text-muted">
                                      {t("Pages.Chatbot.LoadingQuestionsDesc")}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {hasRelative && (
                              <div
                                className={`workspace-tab-pane ${activeTab === "relativeLessons" ? "active" : ""
                                  }`}
                              >
                                <RelativeLessonsPane
                                  relativeLinks={relativeLinks}
                                  relativeLessons={relativeLessonsDetails}
                                  loading={fetchingRelativeLessons}
                                  user={user}
                                  t={t}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Right Panel: Chatbot */}
                  {isStartChatFinished && (
                    <div
                      className={`chatbot-pane-wrapper col-12 ${isChatPaneExpanded ? "col-lg-12" : (isWorkspaceExpanded ? "d-none d-lg-none" : "col-lg-5")
                        } h-100 px-lg-2 mt-3 mt-lg-0 ${mobileActiveTab !== "chat" ? "mobile-hidden" : "mobile-active"
                        }`}
                    >
                      <div className="glass-card">
                        <ChatPane
                          messages={messages}
                          loading={loading}
                          error={error}
                          errorStatus={errorStatus}
                          onResend={handleResend}
                          onSend={(text, pass, mod) =>
                            dispatch(sendChatMessage(text, pass, mod, difficulty))
                          }
                          onStop={() => dispatch(stopChatStream())}
                          requestTimerSeconds={timer.requestTimerSeconds}
                          onClearChat={handleClearChat}
                          onExpandFullScreen={handleExpandChat}
                          onDownload={handleDownloadResult}
                          onToggleStats={(idx) => setExpandedStatsIndex(idx)}
                          isSubChat={true}
                          isStartChatFinished={isStartChatFinished}
                          sessionTitle={sessionTitle}
                          onBackToWelcome={handleNewChat}
                          t={t}
                          totalPrice={totalPrice}
                          passCash={passCash}
                          model={model}
                          onPassCashChange={(val) => setPassCash(val)}
                          onModelChange={(val) => setModel(val)}
                          onDownloadFullChat={handleDownloadFullChat}
                          showKpis={showKpis}
                          onToggleKpis={handleToggleKpis}
                          onToggleSidebar={handleToggleSidebar}
                          isExpanded={isChatPaneExpanded}
                          onToggleExpand={handleToggleChatExpand}
                          showChatStats={showChatStats}
                          isRegeneratingQuestions={isRegeneratingQuestions}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              className="ai-disclaimer text-center py-2 text-muted border-top bg-white"
              style={{
                fontSize: "11px",
                opacity: 0.8,

                width: "100%",
                flexShrink: 0,
              }}
            >
              {t("Pages.Chatbot.AiDisclaimer")}
            </div>
          </div>
        </div>
      </section>
      {/* Modals & Popups */}
      {fullscreenMessageHtml && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          onClick={() => {
            setFullscreenMessageHtml(null);
            setFullscreenMessageQuestion(null);
          }}
        >
          <div
            className="fullscreen-modal-card animate__animated animate__zoomIn animate__faster"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fullscreen-modal-header">
              <h3 className="fullscreen-modal-title">
                <i className="fal fa-robot text-primary"></i>
                {t("Pages.Chatbot.FullScreenTitle")}
              </h3>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="fullscreen-modal-download-btn"
                  onClick={() =>
                    handleDownloadResult(
                      fullscreenMessageHtml,
                      fullscreenMessageQuestion
                    )
                  }
                  title={t("Pages.Chatbot.Download")}
                >
                  <i className="fal fa-download"></i>
                  <span>{t("Pages.Chatbot.Download")}</span>
                </button>
                <button
                  className="fullscreen-modal-close-btn"
                  onClick={() => {
                    setFullscreenMessageHtml(null);
                    setFullscreenMessageQuestion(null);
                  }}
                  title={t("cancel")}
                >
                  <i className="fal fa-times"></i>
                </button>
              </div>
            </div>
            <div
              className="fullscreen-modal-body bot-answer-html"
              dangerouslySetInnerHTML={{ __html: fullscreenMessageHtml }}
            />
          </div>
        </div>
      )}
      {showStreamWaitingPopup && (
        <div className="stream-waiting-overlay animate__animated animate__fadeIn animate__faster">
          <div className="stream-waiting-card animate__animated animate__zoomIn animate__faster">
            <div className="stream-waiting-glow"></div>
            <div className="stream-waiting-spinner-container">
              <div className="stream-waiting-spinner-outer"></div>
              <div className="stream-waiting-spinner-inner"></div>
              <i className="fal fa-robot stream-waiting-icon"></i>
            </div>
            <h3 className="stream-waiting-title">
              {t("Pages.Chatbot.StreamWaitingTitle")}
            </h3>
            <p className="stream-waiting-subtitle">
              {t("Pages.Chatbot.StreamWaitingSubtitle")}
            </p>
            {pendingTopic && (
              <div
                className="stream-waiting-topic mb-3 font-weight-bold text-center animate__animated animate__fadeIn"
                style={{
                  color: "#0e2c53",
                  fontSize: "15px",

                }}
              >
                <i className="fal fa-book-open ml-2 text-primary"></i>
                <span>{t("Pages.Chatbot.LessonSubject", { topic: pendingTopic })}</span>
              </div>
            )}
            {lastMsg && lastMsg.statusText && (
              <div
                className="stream-waiting-status-text mb-3 font-weight-bold text-center animate__animated animate__fadeIn"
                style={{
                  color: "#92278f",
                  fontSize: "15.5px",

                }}
              >
                {lastMsg.statusText}
              </div>
            )}
            <div className="stream-waiting-timer-badge">
              <i className="fal fa-clock ml-2"></i>
              <span>{t("Pages.Chatbot.ElapsedTime")}: </span>
              <span className="mr-1">{timer.requestTimerSeconds}</span>
              <span className="mr-1">{t("Pages.Chatbot.Sec")}</span>
            </div>
            <button
              type="button"
              className="stream-waiting-cancel-btn"
              onClick={() => dispatch(stopChatStream())}
            >
              <i className="fal fa-stop-circle"></i>
              <span>{t("Pages.Chatbot.StreamWaitingCancel")}</span>
            </button>
          </div>
        </div>
      )}
      {(() => {
        let scoreStatus = "NeedFocus";
        if (
          questionsHook.activeQuestions &&
          questionsHook.activeQuestions.length > 0
        ) {
          const totalMarks = questionsHook.activeQuestions.reduce(
            (acc, q) => acc + (q.Mark || 2),
            0
          );
          const achievedMarks = Object.values(
            questionsHook.solvedQuestions
          ).reduce((acc, sq) => acc + sq.userMark, 0);
          const percentage =
            totalMarks > 0 ? Math.round((achievedMarks / totalMarks) * 100) : 0;
          if (percentage >= 90) {
            scoreStatus = "Excellent";
          } else if (percentage >= 75) {
            scoreStatus = "VeryGood";
          } else if (percentage >= 50) {
            scoreStatus = "Acceptable";
          }
        }
        const titleText =
          extractTitleFromHtml(lessonHtml) ||
          sessionTitle ||
          t("Pages.Chatbot.LinguisticGenius");
        return (
          <TrophyModal
            isOpen={showTrophyModal}
            onClose={() => setShowTrophyModal(false)}
            onShowReport={questionsHook.handleFinishAttempt}
            scoreStatus={scoreStatus}
            trophyTitle={titleText}
            t={t}
          />
        );
      })()}
      <CostDetailsModal
        isOpen={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        messages={messages}
        totalPrice={totalPrice || 0}
        t={t}
      />
      <AiStatsModal
        isOpen={expandedStatsIndex !== -1 && messages[expandedStatsIndex] !== undefined}
        onClose={() => setExpandedStatsIndex(-1)}
        stats={messages[expandedStatsIndex]}
        t={t}
      />
      {chatScreen === "workspace" && (
        <div className="mobile-nav-tab-bar d-flex d-lg-none">
          {isStartChatFinished && (
            <button
              className={`mobile-nav-btn ${mobileActiveTab === "chat" ? "active" : ""}`}
              onClick={() => setMobileActiveTab("chat")}
            >
              <i className="fal fa-comment-alt-lines"></i>
              <span>{t("Pages.Chatbot.ChatWithSubbot")}</span>
            </button>
          )}
          <button
            className={`mobile-nav-btn ${mobileActiveTab === "lesson" ? "active" : ""
              }`}
            onClick={() => {
              setMobileActiveTab("lesson");
              setActiveTab("lesson");
            }}
          >
            <i className="fal fa-book-open"></i>
            <span>{t("Pages.Chatbot.GeneratedLesson")}</span>
          </button>
          {showQuestionsTab && (
            <button
              className={`mobile-nav-btn ${mobileActiveTab === "questions" ? "active" : ""
                }`}
              onClick={() => {
                setMobileActiveTab("questions");
                setActiveTab("questions");
              }}
            >
              {questionsHook.activeQuestions &&
                questionsHook.activeQuestions.length > 0 ? (
                <i className="fal fa-graduation-cap"></i>
              ) : (
                <i className="fas fa-spinner fa-spin" style={{ color: "#92278f" }}></i>
              )}
              <span>{t("Pages.Chatbot.InteractiveExercises")}</span>
            </button>
          )}
          {hasRelative && (
            <button
              className={`mobile-nav-btn ${mobileActiveTab === "relativeLessons" ? "active" : ""
                }`}
              onClick={() => {
                setMobileActiveTab("relativeLessons");
                setActiveTab("relativeLessons");
              }}
            >
              {fetchingRelativeLessons ? (
                <i className="fas fa-spinner fa-spin" style={{ color: "#92278f" }}></i>
              ) : (
                <i className="fal fa-link"></i>
              )}
              <span>{t("Pages.Chatbot.RelativeLessonsTab")}</span>
            </button>
          )}
          {messages && messages.length > 0 && (
            <button
              className={`mobile-nav-btn ${mobileActiveTab === "kpis" ? "active" : ""}`}
              onClick={() => setMobileActiveTab("kpis")}
            >
              <i className="fal fa-analytics"></i>
              <span>{t("Pages.Chatbot.ShowKpis") || "مؤشرات الأداء"}</span>
            </button>
          )}
          {topicRecommendation && topicRecommendation.topicName && (
            <button
              className="mobile-nav-btn animate__animated animate__pulse animate__infinite animate__slower"
              onClick={handleOpenNextTopicModal}
              style={{
                background: "rgba(146, 39, 143, 0.05)",
              }}
            >
              <i className="fal fa-rocket text-primary" style={{ color: "#92278f" }}></i>
              <span style={{ color: "#92278f", fontWeight: "bold" }}>
                {t("Pages.Chatbot.NextLearnTab")}
              </span>
            </button>
          )}
        </div>
      )}
      {errorStatus === "unsupported" && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
        >
          <div
            className="fullscreen-modal-card p-4 text-center animate__animated animate__zoomIn animate__faster"
            style={{ maxWidth: "450px", borderRadius: "16px", height: "auto" }}
          >
            <div className="mb-3">
              <i className="fal fa-globe text-warning display-4"></i>
            </div>
            <h3
              className="h5 font-weight-bold mb-3"
              style={{ color: "#0e2c53" }}
            >
              {t("Pages.Chatbot.ChatPaneTitle")}
            </h3>
            <p
              className="text-muted mb-4"
              style={{
                fontSize: "15px",

                lineHeight: "1.6",
              }}
            >
              {t("Pages.Chatbot.ChatUnsupportedError") ||
                "عذراً، لا يمكن للمساعد الإجابة عن موضوع خارج نطاق اللغة العربية."}
            </p>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-primary btn-sm px-5 rounded-pill font-weight-bold"
                onClick={() => {
                  dispatch(clearActiveChat());
                }}
                style={{
                  background: "#92278f",
                  borderColor: "#92278f",

                }}
              >
                {t("cancel") || "إغلاق"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmClear && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
        >
          <div
            className="fullscreen-modal-card p-4 text-center animate__animated animate__zoomIn animate__faster"
            style={{ maxWidth: "400px", borderRadius: "16px", height: "auto" }}
          >
            <div className="mb-3">
              <i className="fal fa-exclamation-triangle text-warning display-4"></i>
            </div>
            <h3
              className="h5 font-weight-bold mb-2"

            >
              {t("Pages.Chatbot.ConfirmTitle")}
            </h3>
            <p className="text-muted mb-4" style={{ fontSize: "14.5px" }}>
              {t("Pages.Chatbot.ConfirmClearChat")}
            </p>
            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-danger btn-sm px-4 rounded-pill font-weight-bold"
                onClick={() => {
                  dispatch(clearActiveChat());
                  setConfirmClear(false);
                  setChatScreen("welcome");
                  setPendingTopic("");
                  questionsHook.resetQuestionsState();
                  lastFetchedRelativeLessonsJson.current = null;
                }}
              >
                {t("confirm")}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 rounded-pill font-weight-bold"
                onClick={() => setConfirmClear(false)}
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
        >
          <div
            className="fullscreen-modal-card p-4 text-center animate__animated animate__zoomIn animate__faster"
            style={{ maxWidth: "400px", borderRadius: "16px", height: "auto" }}
          >
            <div className="mb-3">
              <i className="fal fa-exclamation-triangle text-danger display-4"></i>
            </div>
            <h3
              className="h5 font-weight-bold mb-2"

            >
              {t("Pages.Chatbot.ConfirmTitle")}
            </h3>
            <p className="text-muted mb-4" style={{ fontSize: "14.5px" }}>
              {t("Pages.Chatbot.ConfirmDeleteHistory")}
            </p>
            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-danger btn-sm px-4 rounded-pill font-weight-bold"
                onClick={() => {
                  dispatch(deleteChatHistory());
                  setConfirmDelete(false);
                  setChatScreen("welcome");
                  setPendingTopic("");
                  questionsHook.resetQuestionsState();
                  lastFetchedRelativeLessonsJson.current = null;
                }}
              >
                {t("confirm")}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 rounded-pill font-weight-bold"
                onClick={() => setConfirmDelete(false)}
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {downloadHook.downloadModalOpen && (
        <div
          className="modal-overlay active animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
          onClick={() => downloadHook.setDownloadModalOpen(false)}
        >
          <div
            className="chatbot-modal-content download-modal animate__animated animate__zoomIn animate__faster"
            style={{
              maxWidth: "480px",
              borderRadius: "16px",
              height: "auto",
              padding: "30px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="download-modal-header text-center mb-3">
              <i
                className="fal fa-file-download text-primary mb-2"
                style={{ fontSize: "3.5rem" }}
              ></i>
              <h3
                className="h5 font-weight-bold mb-2"
                style={{ color: "#0e2c53" }}
              >
                {t("Pages.Chatbot.DownloadOptions")}
              </h3>
            </div>

            <div className="download-modal-body w-100 text-right pr-2">
              <div className="mb-4">
                <label
                  className="font-weight-bold d-block mb-2 text-dark"
                  style={{ fontSize: "15px" }}
                >
                  <i className="fal fa-list-ul ml-2 text-primary"></i>
                  {t("Pages.Chatbot.DownloadContentLabel")}
                </label>

                <div
                  className="custom-control custom-checkbox mb-2 pr-3"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    id="opt-explanation"
                    className="custom-control-input"
                    checked={downloadHook.downloadExplanationOption}
                    onChange={(e) =>
                      downloadHook.setDownloadExplanationOption(e.target.checked)
                    }
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="opt-explanation"
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadExplanation")}
                  </label>
                </div>

                <div
                  className="custom-control custom-checkbox mb-2 pr-3"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    id="opt-chat"
                    className="custom-control-input"
                    checked={downloadHook.downloadChatOption}
                    onChange={(e) =>
                      downloadHook.setDownloadChatOption(e.target.checked)
                    }
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="opt-chat"
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadChat")}
                  </label>
                </div>

                <div
                  className="custom-control custom-checkbox mb-2 pr-3"
                  style={{
                    cursor: isQuestionsLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    id="opt-questions"
                    className="custom-control-input"
                    checked={
                      downloadHook.downloadQuestionsOption && !isQuestionsLoading
                    }
                    onChange={(e) => {
                      if (!isQuestionsLoading) {
                        downloadHook.setDownloadQuestionsOption(e.target.checked);
                      }
                    }}
                    disabled={isQuestionsLoading}
                    style={{ cursor: isQuestionsLoading ? "not-allowed" : "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="opt-questions"
                    style={{
                      cursor: isQuestionsLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadQuestions")}
                  </label>
                  {isQuestionsLoading && (
                    <span
                      className="text-warning mx-2 animate__animated animate__flash animate__infinite"
                      title={t("Pages.Chatbot.QuestionsLoadingAlert")}
                      style={{ cursor: "help" }}
                    >
                      <i className="fal fa-exclamation-triangle"></i>
                    </span>
                  )}
                </div>

                {downloadHook.downloadQuestionsOption && !isQuestionsLoading && (
                  <div
                    className="mr-4 pr-3 border-right"
                    style={{ borderColor: "rgba(14, 44, 83, 0.1)" }}
                  >
                    <div
                      className="custom-control custom-checkbox mb-2 pr-3"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        id="opt-solved"
                        className="custom-control-input"
                        checked={downloadHook.downloadSolvedOption}
                        onChange={(e) =>
                          downloadHook.setDownloadSolvedOption(e.target.checked)
                        }
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="custom-control-label pr-4 text-muted"
                        htmlFor="opt-solved"
                        style={{
                          cursor: "pointer",
                          fontSize: "13px",

                        }}
                      >
                        {t("Pages.Chatbot.DownloadIncludeModelAnswers")}
                      </label>
                    </div>

                    <div
                      className="custom-control custom-checkbox mb-2 pr-3"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        id="opt-student-answers"
                        className="custom-control-input"
                        checked={downloadHook.downloadStudentAnswersOption}
                        onChange={(e) =>
                          downloadHook.setDownloadStudentAnswersOption(
                            e.target.checked
                          )
                        }
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="custom-control-label pr-4 text-muted"
                        htmlFor="opt-student-answers"
                        style={{
                          cursor: "pointer",
                          fontSize: "13px",

                        }}
                      >
                        {t("Pages.Chatbot.DownloadIncludeStudentAnswers")}
                      </label>
                    </div>

                    <div
                      className="custom-control custom-checkbox mb-2 pr-3"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        id="opt-hints"
                        className="custom-control-input"
                        checked={downloadHook.downloadHintsOption}
                        onChange={(e) =>
                          downloadHook.setDownloadHintsOption(e.target.checked)
                        }
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="custom-control-label pr-4 text-muted"
                        htmlFor="opt-hints"
                        style={{
                          cursor: "pointer",
                          fontSize: "13px",

                        }}
                      >
                        {t("Pages.Chatbot.DownloadIncludeHints")}
                      </label>
                    </div>
                  </div>
                )}

                <div
                  className="custom-control custom-checkbox mb-2 pr-3"
                  style={{
                    cursor: isRelativeLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    id="opt-relative"
                    className="custom-control-input"
                    checked={
                      downloadHook.downloadRelativeLessonsOption &&
                      !isRelativeLoading
                    }
                    onChange={(e) => {
                      if (!isRelativeLoading) {
                        downloadHook.setDownloadRelativeLessonsOption(
                          e.target.checked
                        );
                      }
                    }}
                    disabled={isRelativeLoading}
                    style={{ cursor: isRelativeLoading ? "not-allowed" : "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="opt-relative"
                    style={{
                      cursor: isRelativeLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadRelativeLessons")}
                  </label>
                  {isRelativeLoading && (
                    <span
                      className="text-warning mx-2 animate__animated animate__flash animate__infinite"
                      title={t("Pages.Chatbot.RelativeLoadingAlert")}
                      style={{ cursor: "help" }}
                    >
                      <i className="fal fa-exclamation-triangle"></i>
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label
                  className="font-weight-bold d-block mb-2 text-dark"
                  style={{ fontSize: "15px" }}
                >
                  <i className="fal fa-file-signature ml-2 text-primary"></i>
                  {t("Pages.Chatbot.DownloadFormatLabel")}
                </label>

                <div
                  className="custom-control custom-radio mb-2 pr-3"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    id="format-html"
                    name="downloadFormat"
                    className="custom-control-input"
                    checked={downloadHook.downloadFormatOption === "html"}
                    onChange={() => downloadHook.setDownloadFormatOption("html")}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="format-html"
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadFormatHtml")}
                  </label>
                </div>

                <div
                  className="custom-control custom-radio mb-2 pr-3"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    id="format-word"
                    name="downloadFormat"
                    className="custom-control-input"
                    checked={downloadHook.downloadFormatOption === "word"}
                    onChange={() => downloadHook.setDownloadFormatOption("word")}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="format-word"
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadFormatWord")}
                  </label>
                </div>

                <div
                  className="custom-control custom-radio mb-2 pr-3"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    id="format-pdf"
                    name="downloadFormat"
                    className="custom-control-input"
                    checked={downloadHook.downloadFormatOption === "pdf"}
                    onChange={() => downloadHook.setDownloadFormatOption("pdf")}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="custom-control-label pr-4 text-muted"
                    htmlFor="format-pdf"
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",

                    }}
                  >
                    {t("Pages.Chatbot.DownloadFormatPdf")}
                  </label>
                </div>
              </div>
            </div>

            {(() => {
              const isDownloadDisabled =
                !downloadHook.downloadExplanationOption &&
                !downloadHook.downloadChatOption &&
                !(downloadHook.downloadQuestionsOption && !isQuestionsLoading) &&
                !(downloadHook.downloadRelativeLessonsOption && !isRelativeLoading);
              return (
                <div className="d-flex justify-content-center gap-3 w-100 mt-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm px-4 rounded-pill font-weight-bold"
                    onClick={() =>
                      downloadHook.performDownload({
                        messages,
                        activeSessionId,
                        history,
                        user,
                        t,
                        activeQuestions: questionsHook.activeQuestions,
                        solvedQuestions: questionsHook.solvedQuestions,
                        examAnswers: questionsHook.examAnswers,
                        FontSize: font.FontSize,
                        relativeLessonsDetails,
                      })
                    }
                    disabled={isDownloadDisabled || downloadHook.isDownloading}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    {downloadHook.isDownloading ? (
                      <>
                        <i className="fas fa-spinner fa-spin ml-1"></i>
                        <span>
                          {t("Pages.Chatbot.Downloading") || "جاري التحميل..."}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{t("Pages.Chatbot.DownloadAction")}</span>
                        <i className="fal fa-download"></i>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-4 rounded-pill font-weight-bold"
                    onClick={() => downloadHook.setDownloadModalOpen(false)}
                    disabled={downloadHook.isDownloading}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    {t("Cancel")}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {showRegenerateConfirm && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
        >
          <div
            className="fullscreen-modal-card p-4 text-right animate__animated animate__zoomIn animate__faster"
            style={{ maxWidth: "450px", borderRadius: "16px", height: "auto" }}
          >
            <div className="d-flex align-items-center mb-3 text-primary">
              <i className="fal fa-sync-alt ml-2 h4 m-0"></i>
              <h3
                className="h5 font-weight-bold m-0"

              >
                {t("Pages.Chatbot.RegenerateConfirmTitle") || "تأكيد إعادة توليد الأسئلة"}
              </h3>
            </div>
            <p className="text-muted mb-4" style={{ fontSize: "14.5px", lineHeight: "1.6" }}>
              {t("Pages.Chatbot.RegenerateConfirmDesc") || "يرجى تحديد الإعدادات المطلوبة للأسئلة الجديدة قبل البدء:"}
            </p>

            {/* Questions Count Slider */}
            <div className="form-group mb-4">
              <label className="font-weight-bold mb-2 d-block" style={{ fontSize: "13.5px", color: "#0e2c53" }}>
                {t("Pages.Chatbot.QuestionsCountSetting") || "عدد الأسئلة التفاعلية المولدة"}
              </label>
              <div className="d-flex align-items-center gap-3">
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={tempQuestionsCount}
                  onChange={(e) => setTempQuestionsCount(parseInt(e.target.value))}
                  className="form-control-range flex-grow-1 ml-3"
                  style={{ accentColor: "#92278f" }}
                />
                <span className="badge badge-primary font-weight-bold px-2.5 py-1.5" style={{ minWidth: "35px", backgroundColor: "#92278f", borderRadius: "12px", fontSize: "13px" }}>
                  {tempQuestionsCount}
                </span>
              </div>
            </div>

            {/* Difficulty Select */}
            <div className="form-group mb-4">
              <label className="font-weight-bold mb-2 d-block" style={{ fontSize: "13.5px", color: "#0e2c53" }}>
                {t("Pages.Chatbot.DifficultyLevel") || "مستوى صعوبة الأسئلة"}
              </label>
              <select
                value={tempDifficulty}
                onChange={(e) => setTempDifficulty(e.target.value)}
                className="form-control"
                style={{ borderRadius: "10px", borderColor: "rgba(14, 44, 83, 0.15)", fontSize: "14.5px" }}
              >
                <option value="easy">{t("Pages.Chatbot.DifficultyEasy") || "سهل"}</option>
                <option value="mid">{t("Pages.Chatbot.DifficultyMid") || "متوسط"}</option>
                <option value="hard">{t("Pages.Chatbot.DifficultyHard") || "صعب"}</option>
              </select>
            </div>

            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <button
                type="button"
                className="btn btn-secondary btn-sm px-4 rounded-pill font-weight-bold ml-2"
                onClick={() => setShowRegenerateConfirm(false)}

              >
                {t("Cancel") || "إغلاق"}
              </button>
              <button
                type="button"
                className="btn btn-success btn-sm px-4 rounded-pill font-weight-bold"
                onClick={handleConfirmRegenerate}
                style={{ backgroundColor: "#92278f", borderColor: "#92278f" }}
              >
                {t("Pages.Chatbot.Confirm") || "تأكيد"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isChangingMode && (
        <div className="stream-waiting-overlay animate__animated animate__fadeIn animate__faster" style={{ zIndex: 99999 }}>
          <div className="stream-waiting-card animate__animated animate__zoomIn animate__faster">
            <div className="stream-waiting-glow"></div>
            <div className="stream-waiting-spinner-container">
              <div className="stream-waiting-spinner-outer"></div>
              <div className="stream-waiting-spinner-inner"></div>
              <i className="fal fa-exchange-alt stream-waiting-icon"></i>
            </div>
            <h3 className="stream-waiting-title">
              {changingModeText}
            </h3>
            <p className="stream-waiting-subtitle">
              {t("Pages.Chatbot.PreparingSession") || "جاري تهيئة الجلسة..."}
            </p>
          </div>
        </div>
      )}
      {isSwitchingHistory && (
        <div className="stream-waiting-overlay animate__animated animate__fadeIn animate__faster" style={{ zIndex: 99999 }}>
          <div className="stream-waiting-card animate__animated animate__zoomIn animate__faster">
            <div className="stream-waiting-glow"></div>
            <div className="stream-waiting-spinner-container">
              <div className="stream-waiting-spinner-outer"></div>
              <div className="stream-waiting-spinner-inner"></div>
              <i className="fal fa-history stream-waiting-icon"></i>
            </div>
            <h3 className="stream-waiting-title">
              {t("Pages.Chatbot.SwitchingHistoryWaiting")}
            </h3>
            <p className="stream-waiting-subtitle">
              {t("Pages.Chatbot.PreparingSession")}
            </p>
          </div>
        </div>
      )}
      {showNextTopicModal && topicRecommendation && (
        <div
          className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster"
          style={{ zIndex: 9999 }}
          onClick={() => setShowNextTopicModal(false)}
        >
          <div
            className="fullscreen-modal-card p-4 text-center animate__animated animate__zoomIn animate__faster position-relative shadow"
            style={{
              maxWidth: "500px",
              borderRadius: "20px",
              height: "auto",
              border: "1px solid rgba(146, 39, 143, 0.15)",
              boxShadow: "0 20px 40px rgba(146, 39, 143, 0.15) !important",
              background: "#fff"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                position: "absolute",
                top: "-20px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "120px",
                height: "120px",
                background: "radial-gradient(circle, rgba(146, 39, 143, 0.2) 0%, rgba(255, 255, 255, 0) 70%)",
                zIndex: 0,
                pointerEvents: "none"
              }}
            ></div>

            <button
              type="button"
              className="close position-absolute"
              style={{
                left: "15px",
                top: "15px",
                border: "none",
                background: "none",
                fontSize: "1.2rem",
                color: "#999",
                cursor: "pointer",
                zIndex: 10,
                width: 'auto'
              }}
              onClick={() => setShowNextTopicModal(false)}
            >
              <i className="fal fa-times"></i>
            </button>

            <div className="mb-3 position-relative" style={{ zIndex: 1 }}>
              <div
                className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle"
                style={{
                  width: "70px",
                  height: "70px",
                  border: "2px solid rgba(146, 39, 143, 0.15)",
                  background: "linear-gradient(135deg, rgba(146, 39, 143, 0.05) 0%, rgba(14, 44, 83, 0.02) 100%)"
                }}
              >
                <i className="fal fa-rocket text-primary animate__animated animate__pulse animate__infinite" style={{ fontSize: "2rem", color: "#92278f" }}></i>
              </div>
            </div>

            <h3
              className="h5 font-weight-bold mb-3"
              style={{ color: "#0e2c53" }}
            >
              {t("Pages.Chatbot.NextLearnModalTitle") || "الخطوة القادمة في مسيرتك التعليمية"}
            </h3>

            {topicRecommendation.introText && (
              <p
                className="text-right text-muted mb-3"
                style={{ fontSize: "14px", lineHeight: "1.6", direction: "rtl" }}
              >
                {topicRecommendation.introText}
              </p>
            )}

            {topicRecommendation.choices && topicRecommendation.choices.length > 0 ? (
              <div className="mb-4" style={{ direction: "rtl", textAlign: "right" }}>
                {topicRecommendation.choices.map((choice, idx) => {
                  const isSelected = selectedRecommendIdx === idx;
                  return (
                    <div
                      key={idx}
                      onClick={() => setSelectedRecommendIdx(idx)}
                      className="p-3 mb-2 rounded transition-all"
                      style={{
                        cursor: "pointer",
                        background: isSelected
                          ? "linear-gradient(135deg, rgba(146, 39, 143, 0.08) 0%, rgba(14, 44, 83, 0.04) 100%)"
                          : "#f8fafc",
                        border: isSelected
                          ? "2px solid #92278f"
                          : "2px solid rgba(14, 44, 83, 0.04)",
                        transform: isSelected ? "scale(1.01)" : "none",
                        boxShadow: isSelected ? "0 4px 15px rgba(146, 39, 143, 0.08)" : "none",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <h4
                          className="h6 font-weight-bold m-0"
                          style={{

                            fontSize: "15.5px",
                            color: isSelected ? "#92278f" : "#0e2c53",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <span
                            className="d-inline-block rounded-circle ml-2"
                            style={{
                              width: "18px",
                              height: "18px",
                              border: isSelected ? "5px solid #92278f" : "2px solid #cbd5e1",
                              background: "#fff",
                              transition: "all 0.15s ease"
                            }}
                          ></span>
                          <span>{choice.topicName}</span>
                        </h4>
                      </div>
                      <p
                        className="text-muted m-0 mt-2 small"
                        style={{ fontSize: "13px", lineHeight: "1.5" }}
                      >
                        {choice.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="p-3 mb-4 rounded"
                style={{
                  background: "linear-gradient(135deg, rgba(146, 39, 143, 0.02) 0%, rgba(14, 44, 83, 0.02) 100%)",
                  border: "1px dashed rgba(146, 39, 143, 0.2)"
                }}
              >
                <h4
                  className="h6 font-weight-bold mb-2 text-dark"
                  style={{ fontSize: "17px", lineHeight: "1.5" }}
                >
                  {topicRecommendation.topicName}
                </h4>
                <p
                  className="text-muted m-0 small"
                  style={{ fontSize: "14px", lineHeight: "1.6" }}
                >
                  {topicRecommendation.description}
                </p>
              </div>
            )}

            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-success btn-sm px-4 rounded-pill font-weight-bold ml-2"
                onClick={handleConfirmNextTopic}
                style={{
                  background: "#92278f",
                  borderColor: "#92278f",
                  fontSize: "14px"
                }}
              >
                <i className="fal fa-play-circle ml-2"></i>
                <span>{t("Pages.Chatbot.NextLearnStartBtn")}</span>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 rounded-pill font-weight-bold"
                onClick={() => setShowNextTopicModal(false)}
                style={{
                  fontSize: "14px",
                  width: 'auto'
                }}
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
